/**
 * Frontend Runtime Config with GraphQL
 * 
 * Purpose: Load configuration from backend GraphQL API at runtime
 * This allows API keys to be rotated without rebuilding frontend
 * 
 * Environment variable mapping:
 * Backend → GraphQL → Frontend
 * - GOOGLE_MAP_API_KEY_CF → googleMapsApiKey
 * - RECAPTCHA_KEY_CF → recaptchaSiteKey
 */

import { APP_CONFIG } from '../graphql/query/config/config'
import client from './configApollo'

class RuntimeConfig {
  constructor() {
    this.config = null
    this.loading = false
    this.error = null
    this.listeners = []
  }

  /**
   * Initialize configuration by fetching from backend via GraphQL
   * @returns {Promise<Object>} Configuration object
   */
  async initialize() {
    if (this.config) {
      return this.config // Already loaded
    }

    if (this.loading) {
      // Wait for ongoing request
      return new Promise((resolve, reject) => {
        this.listeners.push({ resolve, reject })
      })
    }

    this.loading = true

    try {
      // Query appConfig via GraphQL
      const { data } = await client.query({
        query: APP_CONFIG,
        fetchPolicy: 'network-only', // Always fetch fresh from server
      })

      if (!data || !data.appConfig) {
        throw new Error('Invalid configuration response')
      }

      this.config = data.appConfig

      // Validate required fields
      if (!this.config.googleMapsApiKey || !this.config.recaptchaSiteKey) {
        throw new Error('Missing required configuration')
      }

      // Optional: Cache in localStorage with expiry
      this.cacheConfig(this.config)

      // Load reCAPTCHA script dynamically
      this.loadRecaptchaScript().catch(err => {
        console.error('Failed to load reCAPTCHA script:', err)
      })

      // Notify listeners
      this.listeners.forEach(listener => listener.resolve(this.config))
      this.listeners = []

      this.loading = false
      return this.config

    } catch (error) {
      console.error('Failed to initialize config:', error)
      this.error = error
      this.loading = false

      // Try to use cached config as fallback
      const cachedConfig = this.getCachedConfig()
      if (cachedConfig) {
        console.warn('Using cached configuration as fallback')
        this.config = cachedConfig
        return cachedConfig
      }

      // Notify listeners of error
      this.listeners.forEach(listener => listener.reject(error))
      this.listeners = []

      throw error
    }
  }

  /**
   * Get Google Maps API Key
   * @returns {string|null}
   */
  getGoogleMapsApiKey() {
    return this.config?.googleMapsApiKey || null
  }

  /**
   * Get reCAPTCHA Site Key
   * @returns {string|null}
   */
  getRecaptchaSiteKey() {
    return this.config?.recaptchaSiteKey || null
  }

  /**
   * Load reCAPTCHA script dynamically
   * @returns {Promise<void>}
   */
  async loadRecaptchaScript() {
    const siteKey = this.getRecaptchaSiteKey()
    if (!siteKey) {
      console.warn('reCAPTCHA site key not available')
      return
    }

    // Check if script already loaded
    if (window.grecaptcha) {
      return
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'))
      document.head.appendChild(script)
    })
  }

  /**
   * Cache config in localStorage with 5-minute expiry
   * @param {Object} config
   */
  cacheConfig(config) {
    try {
      const cacheData = {
        config,
        timestamp: Date.now(),
        expiresIn: 5 * 60 * 1000, // 5 minutes
      }
      localStorage.setItem('app_config', JSON.stringify(cacheData))
    } catch (error) {
      console.warn('Failed to cache config:', error)
    }
  }

  /**
   * Get cached config if not expired
   * @returns {Object|null}
   */
  getCachedConfig() {
    try {
      const cached = localStorage.getItem('app_config')
      if (!cached) return null

      const { config, timestamp, expiresIn } = JSON.parse(cached)
      const age = Date.now() - timestamp

      if (age < expiresIn) {
        return config
      }

      // Expired, remove from cache
      localStorage.removeItem('app_config')
      return null
    } catch (error) {
      console.warn('Failed to read cached config:', error)
      return null
    }
  }

  /**
   * Check if config is loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.config !== null
  }

  /**
   * Force reload configuration
   * @returns {Promise<Object>}
   */
  async reload() {
    this.config = null
    this.error = null
    return this.initialize()
  }
}

// Export singleton instance
const runtimeConfig = new RuntimeConfig()
export default runtimeConfig
